import React from "react";
import { cn, formatLovelace } from "@/lib/format";

// ---------- Address Stats radial (Total tab) ----------
export const AddressTotalSnapshot = ({ data, amountMode }) => {
  const recv = BigInt(data?.received_sum?.find((a) => a.unit === "lovelace")?.quantity || "0");
  const sent = BigInt(data?.sent_sum?.find((a) => a.unit === "lovelace")?.quantity || "0");
  const denom = recv + sent;
  const recvPct = denom > 0n ? Number((recv * 1000000n) / denom) / 10000 : 0;
  const C = 2 * Math.PI * 54;
  const recvLen = (recvPct / 100) * C;
  const net = recv >= sent ? recv - sent : sent - recv;
  return (
    <div className="relative overflow-hidden rounded-2xl border border-primary/25 bg-card/30 backdrop-blur-md box-glow px-6 py-6" data-testid="total-snapshot">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-70 tracing-beam pointer-events-none" />
      <h3 className="font-heading text-[15px] md:text-[16px] text-[#22d3ee] text-glow-cyan mb-5">Address Stats</h3>
      <div className="flex flex-col md:flex-row items-center gap-8">
        <div className="relative w-[200px] h-[200px] shrink-0">
          <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full">
            <circle cx="100" cy="100" r="92" fill="none" stroke="hsl(264 100% 68% / 0.1)" strokeWidth="1.5" />
            <circle cx="100" cy="100" r="54" fill="none" stroke="hsl(264 100% 68% / 0.07)" strokeWidth="14" />
          </svg>
          <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full -rotate-90">
            <circle cx="100" cy="100" r="54" fill="none" stroke="#fb7185" strokeWidth="14" />
            <circle cx="100" cy="100" r="54" fill="none" stroke="#34d399" strokeWidth="14" strokeLinecap="round" strokeDasharray={`${recvLen} ${C - recvLen}`} />
          </svg>
          <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ animation: "spin 18s linear infinite" }}>
            <circle cx="100" cy="100" r="92" fill="none" stroke="#22d3ee" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="70 508" />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground">Tx Count</span>
            <span className="font-heading text-[22px] tabnum text-foreground mt-1">{Number(data?.tx_count ?? 0).toLocaleString("en-US")}</span>
          </div>
        </div>
        <div className="flex-1 w-full space-y-3">
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"><span className="w-3 h-3 rounded-full bg-emerald-400" /> Received</span>
            <span className="mono tabnum text-emerald-400 text-[14px]">{formatLovelace(recv.toString(), amountMode)}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="inline-flex items-center gap-2 text-[12px] text-muted-foreground"><span className="w-3 h-3 rounded-full bg-rose-400" /> Sent</span>
            <span className="mono tabnum text-rose-400 text-[14px]">{formatLovelace(sent.toString(), amountMode)}</span>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-primary/10">
            <span className="text-[12px] text-muted-foreground">Net Balance</span>
            <span className={cn("mono tabnum text-[14px]", recv >= sent ? "text-emerald-400" : "text-rose-400")}>
              {recv >= sent ? "+" : "−"}{formatLovelace(net.toString(), amountMode)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
